/**
 * @module  experience/hardgoods
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-string-literal */
// tslint:disable:max-file-line-count
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
import { Injectable } from '@angular/core';
// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
// Models and Interfaces
import { TppSession } from '@support/shared/interfaces/tpp-session.interface';
import {
  ChannelSegment,
  Filter,
  FilterOption,
  FilterOptionsSelected,
  Product,
  ProductRequest,
  SortOption,
} from '@support/api/common/product.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import {
  FilterCriteria,
  HardgoodsContent,
} from '@core/aem/interfaces/shop/browse-hardgoods.interface';
// Constants
import {
  HARDGOODS_CONSTANTS,
  ProductTypeEnum,
} from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { APIError } from '@support/api/common/error.interface';
import { ImagesData } from '@experience/shop/shared-browse/components/product-carousel/models/product-carousel.interface';
import { CHANNELS, SETTINGS } from '@environment/settings';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import {
  DeviceProductOfferingIdSearchResults,
  HardgoodRequestPayload,
  HardgoodsDeviceDetails,
} from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import { ApiCache } from '@support/shared/interfaces/api-cache-object.interface';
import { throwError as observableThrowError, of as observableOf, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { AppConfig } from '@root/app.config';

@Injectable()
export class BrowseHardgoodsService {
  /**
   *  used to store the value of selected sort
   */
  public sortOrder: string;
  /**
   *  used to store the selected value
   */
  public selectedValue: string;
  /**
   *  used to store the value of selected category
   */
  public selectedCategory: string;
  /**
   *  used to store the value of filtered api data
   */
  public filteredApiData: Filter[];
  /**
   *  used to store the value of sorted api data
   */
  public sortedApiData: SortOption[];
  /**
   *  used to store the value sorted filtered array
   */
  public storedFilteredArray: string[];
  /**
   *  used to store the set filters
   */
  public setFilters: Filter[];
  /**
   *  used to store previously selected values
   */
  public previousSelectedValues: FilterCriteria[];
  /**
   *  used to store the value of selected sort
   */
  public selectedsortOptionValue: string;
  /**
   *  used to store the value of hardgood data subject
   */
  public hardGoodDataSubject: Subject<HardgoodsDeviceDetails>;
  /**
   *  used to store the value of selected sort observable
   */
  public hardGoodDataObservable$: Observable<HardgoodsDeviceDetails>;
  /**
   *  used to store the value of accessory category subject
   */
  public loadAcccessoryCategorySubject: Subject<boolean>;
  /**
   *  used to store the value of accessory catgory sibject observable
   */
  public loadAcccessoryCategoryObservable$: Observable<boolean>;

  constructor(
    private apiGateway: ApiGatewayService,
    private contentProviderService: ContentProviderService,
    private sharedBrowseService: SharedBrowseService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private featureToggleUtilService: FeatureToggleUtilService,
    private objectService: ObjectService,
    private appConfig: AppConfig
  ) {
    this.sortOrder = 'ascending';
    this.selectedsortOptionValue = 'relevance';
    this.selectedValue = '';
    this.selectedCategory = '';
    this.filteredApiData = [];
    this.sortedApiData = [];
    this.storedFilteredArray = [];
    this.previousSelectedValues = [];
    this.selectedsortOptionValue = 'relevance';
    this.hardGoodDataSubject = new Subject();
    this.hardGoodDataObservable$ = this.hardGoodDataSubject.asObservable();
    this.loadAcccessoryCategorySubject = new Subject();
    this.loadAcccessoryCategoryObservable$ = this.loadAcccessoryCategorySubject.asObservable();
  }

  public getHomePage(): string {
    const tppSession = sessionStorage.getItem('tpp_session');
    const newTppSession: TppSession = (tppSession && JSON.parse(tppSession)) || {};
    newTppSession.address = this.stateStoreUtilsService.getBillingAddress();
    sessionStorage.setItem('tpp_session', JSON.stringify(newTppSession));
    const brand = this.stateStoreUtilsService.getBrand();
    return this.appConfig.getConfig(`${brand}_path`) || '/';
  }

  /**
   * @ngdoc method
   * @name loadHardgoodsData
   * @param selectedRoute string with active route url,
   * @param pageNumber number of the current Page
   * @param isUpgrade boolean from route params indicating Upgrade Flow
   * @returns Observable<object>
   * @description Method to load hardgoods if customer config data is available
   */
  public loadHardgoodsData(
    selectedRoute: string,
    pageNumber: number,
    isUpgrade?: boolean,
    category?: string,
    offeringId?: string
  ): Observable<HardgoodsDeviceDetails> {
    const customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    const isZipCodeAvailable = !!this.objectService.get(
      this.stateStoreUtilsService.getBillingAddress(),
      'postalCode'
    );
    let requestPayload: HardgoodRequestPayload;
    if (!this.objectService.get(customerConfigData, 'zip') && !isZipCodeAvailable) {
      return observableOf(null);
    } else {
      requestPayload = this.getHardgoodRequestPayload(
        selectedRoute,
        pageNumber.toString(),
        isUpgrade,
        category,
        offeringId
      );
      requestPayload = this.getFilterRequest(requestPayload);
      selectedRoute = this.getAccessoryRouteUrl(selectedRoute);
      if (selectedRoute === HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES && !category) {
        requestPayload.includeFilters = true;
        return this.getAccessoryList(requestPayload);
      } else {
        return this.getHardgoodsList(requestPayload);
      }
    }
  }

  /**
   * @ngdoc method
   * @name getHardgoodsList
   * @param reqObj request payload to get hardgood offers list
   * @param isUpgrade boolean from route params indicating Upgrade Flow
   * @returns Observable<object>
   * @description Method to call API to load hardgood offers
   */
  public getHardgoodsList(reqObj: HardgoodRequestPayload): Observable<HardgoodsDeviceDetails> {
    // const isRetail = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL;
    // const showUpdatedBrowseDevices = isRetail;
    const cacheObj: ApiCache = {
      cacheKey: JSON.stringify(reqObj).concat(this.stateStoreUtilsService.getInteractionId()),
      cacheResponse: true,
      preferCache: true,
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2100_retrieveProducts, reqObj, null, null, null, cacheObj)
      .pipe(
        map((response: HttpResponse<HardgoodsDeviceDetails>) => {
          const res = response.body;
          let filtersData = res['filters'];
          if (!this.checkFilterApplied()) {
            this.updateDefaultFilterCache(filtersData);
          } else {
            filtersData = this.formatFilterResponse();
          }
          const hardgoodDetails = {
            filter: filtersData,
            sort: res['sortOptions'],
            productModels: [],
            isProductAvailable: this.objectService.get(res, 'metadata.isProductAvailable'),
            metadata: this.objectService.get(res, 'metadata'),
          };
          if (res['products']) {
            for (const product of res['products']) {
              // hardgoodDetails.productModels.push(new BrowseDeviceModel(product, this.objectService,
              //   SETTINGS.EXT_IMAGE_URL, showUpdatedBrowseDevices));
              hardgoodDetails.productModels.push(
                new BrowseDeviceModel(product, this.objectService, SETTINGS.EXT_IMAGE_URL)
              );
            }
          }
          return hardgoodDetails;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * @ngdoc method
   * @name getAccessoryList
   * @param reqObj request payload to get hardgood offers list
   * @param isUpgrade boolean from route params indicating Upgrade Flow
   * @returns Observable<object>
   * @description Method to call API to load hardgood offers
   */
  public getAccessoryList(reqObj: HardgoodRequestPayload): Observable<HardgoodsDeviceDetails> {
    const cacheObj: ApiCache = {
      cacheKey: JSON.stringify(reqObj).concat(this.stateStoreUtilsService.getInteractionId()),
      cacheResponse: true,
      preferCache: true,
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2166_retrieveProductCategories, reqObj, null, null, null, cacheObj)
      .pipe(
        map((response: HttpResponse<Object>) => {
          const res = response.body;
          const hardgoodDetails = {
            // tslint:disable-next-line: no-string-literal
            filter: res['filters'],
            sort: res['sortOptions'],
            productModels: [],
            isProductAvailable: this.objectService.get(res, 'metadata.isProductAvailable'),
            metadata: this.objectService.get(res, 'metadata'),
          };
          if (res['categories']) {
            for (const category of res['categories']) {
              const categoryList = [];
              for (const product of category.products) {
                categoryList.push(
                  new BrowseDeviceModel(product, this.objectService, SETTINGS.EXT_IMAGE_URL)
                );
              }
              hardgoodDetails.productModels.push({
                categoryName: this.objectService.get(category, 'categoryName'),
                categoryCount: this.objectService.get(category, 'categoryCount'),
                products: categoryList,
              });
            }
          }
          return hardgoodDetails;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * @ngdoc method
   * @name loadAccessoriesCarousel
   * @param selectedRoute string
   * @returns Observable<object>
   * @description Method to call API to load hardgood offers
   */
  public loadAccessoriesCarousel(
    selectedRoute: string,
    pageNumber?: string
  ): Observable<ImagesData> {
    const customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    if (!this.objectService.get(customerConfigData, 'zip')) {
      return observableOf(null);
    } else {
      const requestPayload = this.getHardgoodRequestPayload(selectedRoute, pageNumber);
      const cacheObj: ApiCache = {
        cacheKey: JSON.stringify(requestPayload).concat(
          this.stateStoreUtilsService.getInteractionId()
        ),
        cacheResponse: true,
        preferCache: true,
      };
      return this.apiGateway
        .postRequest(API_MAP.API_2100_retrieveProducts, requestPayload, null, null, null, cacheObj)
        .pipe(
          map((response: HttpResponse<Object>) => {
            return response.body;
          }),
          catchError((error: APIError) => {
            return observableThrowError(error);
          })
        );
    }
  }

  /**
   * @param {string} deviceName
   * @param {string} size
   * @returns {Observable<ImagesData>}
   */
  public searchProductOfferingIdsForSearchedDevice(
    deviceName: string,
    size: number
  ): Observable<DeviceProductOfferingIdSearchResults[]> {
    const payLoad = {
      searchCriteria: deviceName,
      size: size,
    };
    return this.apiGateway.postRequest(API_MAP.API_2160_searchProducts, payLoad, null, null).pipe(
      map((response: HttpResponse<Object>) => {
        return this.objectService.get(response.body, 'SearchProductsResult', []);
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * @ngdoc method
   * @name getHardgoodRequestPayload
   * @param hardgoodType string - device/tablet/accessoty
   * @param customerConfigData customer - zip, billingtype, credit level
   * @param isUpgrade boolean from route params indicating Upgrade Flow
   * @returns HardgoodRequestPayload
   * @description Method to create API payload
   */
  public getHardgoodRequestPayload(
    selectedRoute: string,
    pageNumber: string,
    isUpgrade?: boolean,
    category?: string,
    productOfferingId: string = ''
  ): ProductRequest {
    let productType: string;
    let productSubType: string[];
    let retrieveStoreId: string;
    const channelSegment: ChannelSegment = {};
    switch (SETTINGS.CHANNEL) {
      case CHANNELS.CARE:
        channelSegment.salesChannelCode = HARDGOODS_CONSTANTS.SALESCHANNELCODE_CARE;
        channelSegment.salesSubChannelCode = HARDGOODS_CONSTANTS.SALESSUBCHANNELCODE_CARE;
        channelSegment.subChannelCategory = HARDGOODS_CONSTANTS.SUBCHANNELCATEGORY;
        break;
      case CHANNELS.RETAIL:
        channelSegment.salesChannelCode = HARDGOODS_CONSTANTS.SALES_CHANNEL_CODE_RETAIL;
        channelSegment.salesSubChannelCode = HARDGOODS_CONSTANTS.SALES_SUB_CHANNEL_CODE_RETAIL;
        channelSegment.subChannelCategory = HARDGOODS_CONSTANTS.SUB_CHANNEL_CATEGORY_RETAIL;
        retrieveStoreId = this.stateStoreUtilsService.getStoreId();
        break;
      case CHANNELS.WEB:
        channelSegment.salesChannelCode = HARDGOODS_CONSTANTS.SALES_CHANNEL_CODE_WEB;
        channelSegment.salesSubChannelCode = HARDGOODS_CONSTANTS.SALES_SUB_CHANNEL_CODE_WEB;
        channelSegment.subChannelCategory = HARDGOODS_CONSTANTS.SUB_CHANNEL_CATEGORY_WEB;
        break;
      default:
        break;
    }
    const customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    const zip =
      this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode') ||
      customerConfigData.zip;
    selectedRoute = this.getAccessoryRouteUrl(selectedRoute);
    selectedRoute =
      selectedRoute.indexOf('?') > -1
        ? selectedRoute.slice(0, selectedRoute.indexOf('?'))
        : selectedRoute;
    switch (selectedRoute) {
      case HARDGOODS_CONSTANTS.BROWSE_DEVICES:
        productType = HARDGOODS_CONSTANTS.DEVICEPRODUCTTYPE;
        productSubType = [HARDGOODS_CONSTANTS.DEVICEPRODUCTSUBTYPE];
        break;
      case HARDGOODS_CONSTANTS.BROWSE_TABLETS:
        productType = HARDGOODS_CONSTANTS.DEVICEPRODUCTTYPE;
        productSubType = [HARDGOODS_CONSTANTS.MOBILEINTERNETPRODUCTSUBTYPE];
        break;
      case HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES:
        productType = ProductTypeEnum[1];
        productSubType = [];
        break;
      case HARDGOODS_CONSTANTS.UPGARDE_DEVICES:
        productType = ProductTypeEnum[0];
        productSubType = [];
        break;
      default:
        productType = 'Device';
        productSubType = ['Handset'];
        break;
    }
    return this.getPayload(
      productType,
      productSubType,
      channelSegment,
      pageNumber,
      zip,
      isUpgrade,
      category,
      productOfferingId
    );
  }

  /**
   * Get filters  selected
   * @returns string[]
   */
  public getFiltersRequested(): string[] {
    const filters = [];
    if (this.setFilters) {
      for (const filter of this.setFilters) {
        const filterOptions = [];
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            filterOptions.push(filter.displayName);
          }
        }
        filters.push(filterOptions);
      }
    } else {
      filters.push(HARDGOODS_CONSTANTS.ALL);
    }
    return filters;
  }

  /**
   * Get filter options selected
   * @returns FilterOptionsSelected
   */
  public getFilterOptionsSelected(): FilterOptionsSelected[] {
    const filterOptions = [];
    if (this.checkFilterApplied()) {
      for (const filter of this.setFilters) {
        const selectedFilter = [];
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            selectedFilter.push(option.value);
          }
        }
        if (selectedFilter.length > 0) {
          filterOptions.push({
            fieldName: filter.displayName,
            values: selectedFilter,
          });
        }
      }
    } else {
      filterOptions.push({
        fieldName: 'all',
        values: ['all'],
      });
    }
    return filterOptions;
  }

  /**
   * Check if filters are applied
   * @returns boolean
   */
  public checkFilterApplied(): boolean {
    let isFilterApplied = false;
    if (this.setFilters) {
      for (const filter of this.setFilters) {
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            isFilterApplied = true;
            break;
          }
        }
      }
    }
    return isFilterApplied;
  }

  /**
   * gets the AEM content for browse hardgoods landing pages
   * @returns HardgoodsContent
   */
  public getConfigurableContent(url?: string): Observable<HardgoodsContent | object> {
    return this.contentProviderService.getContentData(url, 'postpaid').pipe(
      map((contentData: object) => {
        const aemContentData = {
          configurableData: null,
          authorableData: null,
        };
        aemContentData.configurableData = <ConfigData>(
          this.contentProviderService.getConfigurableData(contentData)
        );
        aemContentData.authorableData = this.contentProviderService.getAuthorableData(contentData);
        return aemContentData;
      })
    );
  }

  /**
   * Function saves the product details model on selection of device
   * @param offerId : offerId for product
   * @param hardgoodData: HardgoodOffer model
   */
  public saveProductDetails(hardgoodData: Product): void {
    this.stateStoreUtilsService.setProductDetails(hardgoodData);
  }

  /**
   * Function to retrieve payload for 2100
   * @param productType
   * @param productSubType
   * @param channelSegment
   * @param pageNumber
   * @param zip
   * @param isUpgrade boolean from route params indicating Upgrade Flow
   */

  private getPayload(
    productType: string,
    productSubType: string[],
    channelSegment: ChannelSegment,
    pageNumber: string,
    zip: string,
    isUpgrade: boolean,
    category: string = '',
    productOfferingId: string = ''
  ): ProductRequest {
    return {
      productTypes: [productType],
      productOfferingIds: [productOfferingId],
      productSubTypes:
        isUpgrade && SETTINGS.CHANNEL.toLocaleLowerCase() !== CHANNELS.RETAIL
          ? [
              HARDGOODS_CONSTANTS.DEVICEPRODUCTSUBTYPE,
              HARDGOODS_CONSTANTS.MOBILEINTERNETPRODUCTSUBTYPE,
            ]
          : productSubType,
      channelSegment: channelSegment,
      includeFilters: false,
      includeCategories: true,
      category: [category],
      accountId: this.stateStoreUtilsService.getAddFan()
        ? ''
        : this.stateStoreUtilsService.getAccountId(),
      customerId: this.stateStoreUtilsService.getBscsCustomerID(),
      lineId: isUpgrade ? this.stateStoreUtilsService.getLineId() : '',
      orderTypes: [HARDGOODS_CONSTANTS.ACTIVATION],
      offerTypes: [HARDGOODS_CONSTANTS.HARDGOOD],
      offerSubTypes: [HARDGOODS_CONSTANTS.BASE],
      pageSize: HARDGOODS_CONSTANTS.PAGESIZE,
      pageNumber,
      postalCode: zip,
      creditLevels: [this.stateStoreUtilsService.getCreditLevel()],
      sortOptions: [
        {
          sortDirection: this.selectedsortOptionValue,
          sortOptionValue: HARDGOODS_CONSTANTS.ASCENDING,
        },
      ],
      customerSegments: [],
      accountSegments: [],
      filtersRequested: [],
      filterOptionsSelected: this.getFilterOptionsSelected(),
    };
  }

  /**
   * Function to get Accessory route url
   */
  private getAccessoryRouteUrl(selectedRoute: string): string {
    switch (selectedRoute.split('?')[0]) {
      case HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES:
      case HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY:
      case HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY_DETAILS:
        return HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES;
      default:
        return selectedRoute;
    }
  }

  /**
   * Function to retrieve payload for 2100
   */
  private getFilterRequest(requestPayload: HardgoodRequestPayload): HardgoodRequestPayload {
    if (this.setFilters) {
      for (const filter of this.setFilters) {
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            if (
              !requestPayload.hasOwnProperty(filter.fieldName) ||
              (requestPayload.hasOwnProperty(filter.fieldName) &&
                requestPayload[filter.fieldName][0] === '')
            ) {
              requestPayload[filter.fieldName] = [];
            }
            requestPayload[filter.fieldName].push(option.value);
          }
        }
      }
    }
    return requestPayload;
  }

  private updateDefaultFilterCache(filtersData: Filter[]): void {
    this.stateStoreUtilsService.setDefaultFilterCache(filtersData.slice());
  }

  private formatFilterResponse(): Filter[] {
    let updatedCache = this.stateStoreUtilsService.getDefaultFilterCache().slice();
    updatedCache = updatedCache.map((item: Filter) => {
      if (item.filterOptions) {
        item.filterOptions.forEach((option: FilterOption) => {
          option.isSelected = false;
        });
        return item;
      }
    });
    for (const filter of this.setFilters) {
      for (const option of filter.filterOptions) {
        if (option.isSelected) {
          updatedCache = this.updateDefaultFilterCacheItems(
            updatedCache,
            filter.displayName,
            option.displayName,
            option.isSelected
          );
        }
      }
    }
    return updatedCache;
  }

  private updateDefaultFilterCacheItems(
    defaultFilter: Filter[],
    filterName: string,
    optionName: string,
    isSelected: boolean
  ): Filter[] {
    for (const filter of defaultFilter) {
      if (filter.displayName === filterName) {
        for (const option of filter.filterOptions) {
          if (option.displayName === optionName) {
            option.isSelected = isSelected;
          }
        }
      }
    }
    return defaultFilter;
  }
}
